/**
 * The four sync stores, scoped to one account.
 *
 * Every method takes the account from the store instance rather than from the
 * caller, so "read only this learner's queue" is a property of the object rather
 * than a rule each call site has to remember. A memory implementation satisfies
 * the same interface, which is what lets the engine be tested without IndexedDB
 * and lets the app degrade to a working session when storage is unavailable.
 */

import { STORES, isIndexedDbAvailable, openDb, request, transact } from '../storage/db';
import {
  revisionKey,
  type ConflictRecord,
  type OutboxOperation,
  type RemoteRevision,
  type SyncKind,
  type SyncMetaRecord,
} from './types';

export interface SyncStores {
  readonly accountId: string;

  meta(): Promise<SyncMetaRecord>;
  setMeta(patch: Partial<SyncMetaRecord>): Promise<SyncMetaRecord>;

  /** Oldest first: an edit must not overtake the create it depends on. */
  pending(): Promise<OutboxOperation[]>;
  enqueue(operation: OutboxOperation): Promise<void>;
  /** Removed only after a settled acknowledgement. */
  settle(operationId: string): Promise<void>;
  markAttempt(operationId: string, error?: string): Promise<void>;

  /** What the two sides last agreed on, or null if they never have. */
  binding(kind: SyncKind, recordId: string): Promise<RemoteRevision | null>;
  bind(
    kind: SyncKind,
    recordId: string,
    revision: number,
    localRevision: number | null,
  ): Promise<void>;

  conflicts(): Promise<ConflictRecord[]>;
  addConflict(conflict: ConflictRecord): Promise<void>;
  resolveConflict(id: string): Promise<void>;

  /** Everything for this account. Used when a learner clears local data. */
  clear(): Promise<void>;
}

function emptyMeta(accountId: string): SyncMetaRecord {
  return { accountId, cursor: null, reconciled: false, lastPulledAt: null, lastPushedAt: null };
}

// ------------------------------------------------------------------ IndexedDB

class IdbSyncStores implements SyncStores {
  constructor(readonly accountId: string) {}

  async meta(): Promise<SyncMetaRecord> {
    const db = await openDb();
    const row = await transact(db, STORES.syncMeta, 'readonly', (tx) =>
      request(
        tx.objectStore(STORES.syncMeta).get(this.accountId) as IDBRequest<
          SyncMetaRecord | undefined
        >,
      ),
    );
    return row ?? emptyMeta(this.accountId);
  }

  async setMeta(patch: Partial<SyncMetaRecord>): Promise<SyncMetaRecord> {
    const next = { ...(await this.meta()), ...patch, accountId: this.accountId };
    const db = await openDb();
    await transact(db, STORES.syncMeta, 'readwrite', (tx) => {
      tx.objectStore(STORES.syncMeta).put(next);
    });
    return next;
  }

  async pending(): Promise<OutboxOperation[]> {
    const db = await openDb();
    const rows = await transact(db, STORES.outbox, 'readonly', (tx) =>
      request(
        tx.objectStore(STORES.outbox).index('accountId').getAll(this.accountId) as IDBRequest<
          OutboxOperation[]
        >,
      ),
    );
    return rows.sort((a, b) => a.queuedAt - b.queuedAt);
  }

  async enqueue(operation: OutboxOperation): Promise<void> {
    const db = await openDb();
    await transact(db, STORES.outbox, 'readwrite', (tx) => {
      tx.objectStore(STORES.outbox).put(operation);
    });
  }

  async settle(operationId: string): Promise<void> {
    const db = await openDb();
    await transact(db, STORES.outbox, 'readwrite', (tx) => {
      tx.objectStore(STORES.outbox).delete(operationId);
    });
  }

  async markAttempt(operationId: string, error?: string): Promise<void> {
    const db = await openDb();
    await transact(db, STORES.outbox, 'readwrite', async (tx) => {
      const store = tx.objectStore(STORES.outbox);
      const row = await request(store.get(operationId) as IDBRequest<OutboxOperation | undefined>);
      if (!row) return;
      store.put({ ...row, attempts: row.attempts + 1, lastError: error });
    });
  }

  async binding(kind: SyncKind, recordId: string): Promise<RemoteRevision | null> {
    const db = await openDb();
    const row = await transact(db, STORES.remoteRevisions, 'readonly', (tx) =>
      request(
        tx
          .objectStore(STORES.remoteRevisions)
          .get(revisionKey(this.accountId, kind, recordId)) as IDBRequest<
          RemoteRevision | undefined
        >,
      ),
    );
    return row ?? null;
  }

  async bind(
    kind: SyncKind,
    recordId: string,
    revision: number,
    localRevision: number | null,
  ): Promise<void> {
    const db = await openDb();
    await transact(db, STORES.remoteRevisions, 'readwrite', (tx) => {
      tx.objectStore(STORES.remoteRevisions).put({
        key: revisionKey(this.accountId, kind, recordId),
        accountId: this.accountId,
        kind,
        recordId,
        revision,
        localRevision,
        at: Date.now(),
      } satisfies RemoteRevision);
    });
  }

  async conflicts(): Promise<ConflictRecord[]> {
    const db = await openDb();
    const rows = await transact(db, STORES.conflicts, 'readonly', (tx) =>
      request(
        tx.objectStore(STORES.conflicts).index('accountId').getAll(this.accountId) as IDBRequest<
          ConflictRecord[]
        >,
      ),
    );
    return rows.filter((row) => !row.resolved).sort((a, b) => b.detectedAt - a.detectedAt);
  }

  async addConflict(conflict: ConflictRecord): Promise<void> {
    const db = await openDb();
    await transact(db, STORES.conflicts, 'readwrite', (tx) => {
      tx.objectStore(STORES.conflicts).put(conflict);
    });
  }

  async resolveConflict(id: string): Promise<void> {
    const db = await openDb();
    await transact(db, STORES.conflicts, 'readwrite', async (tx) => {
      const store = tx.objectStore(STORES.conflicts);
      const row = await request(store.get(id) as IDBRequest<ConflictRecord | undefined>);
      if (row) store.put({ ...row, resolved: true });
    });
  }

  async clear(): Promise<void> {
    const db = await openDb();
    for (const operation of await this.pending()) await this.settle(operation.operationId);
    await transact(db, [STORES.syncMeta, STORES.conflicts, STORES.remoteRevisions], 'readwrite',
      async (tx) => {
        tx.objectStore(STORES.syncMeta).delete(this.accountId);
        for (const store of [STORES.conflicts, STORES.remoteRevisions] as const) {
          const keys = await request(
            tx.objectStore(store).index('accountId').getAllKeys(this.accountId) as IDBRequest<
              IDBValidKey[]
            >,
          );
          for (const key of keys) tx.objectStore(store).delete(key);
        }
      },
    );
  }
}

// ---------------------------------------------------------------------- memory

class MemorySyncStores implements SyncStores {
  private metaRow: SyncMetaRecord;
  private outbox = new Map<string, OutboxOperation>();
  private revisions = new Map<string, RemoteRevision>();
  private conflictRows = new Map<string, ConflictRecord>();

  constructor(readonly accountId: string) {
    this.metaRow = emptyMeta(accountId);
  }

  async meta() {
    return this.metaRow;
  }
  async setMeta(patch: Partial<SyncMetaRecord>) {
    this.metaRow = { ...this.metaRow, ...patch, accountId: this.accountId };
    return this.metaRow;
  }
  async pending() {
    return [...this.outbox.values()].sort((a, b) => a.queuedAt - b.queuedAt);
  }
  async enqueue(operation: OutboxOperation) {
    this.outbox.set(operation.operationId, operation);
  }
  async settle(operationId: string) {
    this.outbox.delete(operationId);
  }
  async markAttempt(operationId: string, error?: string) {
    const row = this.outbox.get(operationId);
    if (row) this.outbox.set(operationId, { ...row, attempts: row.attempts + 1, lastError: error });
  }
  async binding(kind: SyncKind, recordId: string) {
    return this.revisions.get(revisionKey(this.accountId, kind, recordId)) ?? null;
  }
  async bind(kind: SyncKind, recordId: string, revision: number, localRevision: number | null) {
    const key = revisionKey(this.accountId, kind, recordId);
    this.revisions.set(key, {
      key,
      accountId: this.accountId,
      kind,
      recordId,
      revision,
      localRevision,
      at: Date.now(),
    });
  }
  async conflicts() {
    return [...this.conflictRows.values()]
      .filter((row) => !row.resolved)
      .sort((a, b) => b.detectedAt - a.detectedAt);
  }
  async addConflict(conflict: ConflictRecord) {
    this.conflictRows.set(conflict.id, conflict);
  }
  async resolveConflict(id: string) {
    const row = this.conflictRows.get(id);
    if (row) this.conflictRows.set(id, { ...row, resolved: true });
  }
  async clear() {
    this.metaRow = emptyMeta(this.accountId);
    this.outbox.clear();
    this.revisions.clear();
    this.conflictRows.clear();
  }
}

export function createSyncStores(accountId: string): SyncStores {
  if (!isIndexedDbAvailable()) return new MemorySyncStores(accountId);
  return new IdbSyncStores(accountId);
}

export function createMemorySyncStores(accountId: string): SyncStores {
  return new MemorySyncStores(accountId);
}
